import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Photo as PhotoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import customFrameService, { CreateCustomFrameData, UpdateCustomFrameData } from '../../services/customFrameService';
import { CustomFrame, PhotoPosition } from '../../types';
import PhotoPositionEditor from './PhotoPositionEditor';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import { userCardEnterSx } from '../shared/UserPageShell';
import { useI18n } from '../../i18n/useI18n';

const LIMIT_PER_PAGE = 20;
const FRAME_SIZE_PRIORITY = ['2x4', '4x2', '2x6', '6x2', '4x6', '6x4', '3x4', '4x3'];

const normalizeFrameSize = (sizeValue?: string) =>
  String(sizeValue || '').trim().toLowerCase();

const getFrameSizeKey = (sizeValue?: string) => {
  const normalized = normalizeFrameSize(sizeValue);
  const matched = normalized.match(/\d+x\d+/);
  return matched ? matched[0] : normalized;
};

const CustomFramePage: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [frames, setFrames] = useState<CustomFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const isHydratingAllRef = useRef(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<CustomFrame | null>(null);

  const [formData, setFormData] = useState<CreateCustomFrameData>({
    name: '',
    size: '2x4',
    photoPositions: [],
    link: '',
  });

  const [selectedFrameSizes, setSelectedFrameSizes] = useState<string[]>([]);

  const loadFrames = useCallback(async (page: number = 1, isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await customFrameService.getCustomFrames({
        page,
        limit: LIMIT_PER_PAGE,
      });

      if (isInitial) {
        setFrames(response.frames);
      } else {
        setFrames((prev) => [...prev, ...response.frames]);
      }

      if (response.pagination) {
        setPagination(response.pagination);
        setHasMore(page < response.pagination.pages);
      } else {
        setHasMore(response.frames.length === LIMIT_PER_PAGE);
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load frames');
      toast.error('Failed to load custom frames');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadMoreFrames = useCallback(() => {
    if (!loadingMore && hasMore && !isHydratingAllRef.current) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadFrames(nextPage, false);
    }
  }, [currentPage, hasMore, loadingMore, loadFrames]);

  const loadAllRemainingFrames = useCallback(async () => {
    if (isHydratingAllRef.current || loading || loadingMore || !hasMore) {
      return;
    }

    try {
      isHydratingAllRef.current = true;
      setLoadingMore(true);

      let page = currentPage + 1;
      let keepLoading = true;
      let totalPages = pagination?.pages;

      while (keepLoading) {
        const response = await customFrameService.getCustomFrames({
          page,
          limit: LIMIT_PER_PAGE,
        });

        setFrames((prev) => {
          const seen = new Set(prev.map((item) => item._id));
          const merged = [...prev];
          (response.frames || []).forEach((item) => {
            if (!seen.has(item._id)) {
              seen.add(item._id);
              merged.push(item);
            }
          });
          return merged;
        });

        if (response.pagination) {
          totalPages = response.pagination.pages;
          keepLoading = page < response.pagination.pages;
        } else {
          keepLoading = (response.frames || []).length === LIMIT_PER_PAGE;
        }

        page += 1;
      }

      if (totalPages) {
        setCurrentPage(totalPages);
      }
      setHasMore(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load all frames for filtering');
    } finally {
      isHydratingAllRef.current = false;
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loading, loadingMore, pagination?.pages]);

  useEffect(() => {
    setCurrentPage(1);
    setFrames([]);
    setHasMore(true);
    loadFrames(1, true);
  }, [loadFrames]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreFrames();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreFrames]);

  const handleCreateFrame = async () => {
    try {
      if (!formData.photoPositions.length) {
        toast.error('Please add at least one photo position');
        return;
      }

      await customFrameService.createCustomFrame(formData);
      toast.success('Custom frame created successfully');
      setCreateDialogOpen(false);
      resetForm();
      setCurrentPage(1);
      setFrames([]);
      setHasMore(true);
      loadFrames(1, true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create frame');
    }
  };

  const handleUpdateFrame = async () => {
    if (!selectedFrame) return;

    try {
      const updateData: UpdateCustomFrameData = {
        name: formData.name,
        size: formData.size,
        photoPositions: formData.photoPositions,
        link: formData.link,
      };

      await customFrameService.updateCustomFrame(selectedFrame._id, updateData);
      toast.success('Custom frame updated successfully');
      setEditDialogOpen(false);
      resetForm();
      setCurrentPage(1);
      setFrames([]);
      setHasMore(true);
      loadFrames(1, true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update frame');
    }
  };

  const handleDeleteFrame = async () => {
    if (!selectedFrame) return;

    try {
      await customFrameService.deleteCustomFrame(selectedFrame._id);
      toast.success('Custom frame deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedFrame(null);
      setCurrentPage(1);
      setFrames([]);
      setHasMore(true);
      loadFrames(1, true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete frame');
    }
  };

  const openEditDialog = (frame: CustomFrame) => {
    setSelectedFrame(frame);
    setFormData({
      name: frame.name,
      size: frame.size,
      photoPositions: frame.photoPositions,
      link: frame.link || '',
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (frame: CustomFrame) => {
    setSelectedFrame(frame);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      size: '2x4',
      photoPositions: [],
      link: '',
    });
    setSelectedFrame(null);
  };

  const handlePhotoPositionsChange = (positions: PhotoPosition[]) => {
    setFormData((prev) => ({ ...prev, photoPositions: positions }));
  };

  const getSizeLabel = (size: string) => {
    const sizeMap: Record<string, string> = {
      '2x4': '2x4 Portrait',
      '4x2': '4x2 Landscape',
      '2x6': '2x6 Portrait',
      '6x2': '6x2 Landscape',
      '4x6': '4x6 Portrait',
      '6x4': '6x4 Landscape',
      '3x4': '3x4 Portrait',
      '4x3': '4x3 Landscape',
    };
    return sizeMap[size] || size;
  };

  const getSizeColor = (size: string) => {
    const normalizedSize = normalizeFrameSize(size);
    const colorMap: Record<string, string> = {
      '2x4': '#FF6B6B',
      '4x2': '#FFD93D',
      '2x6': '#6BCF7F',
      '6x2': '#4ECDC4',
      '4x6': '#A8E6CF',
      '6x4': '#FF8B94',
      '3x4': '#DDA0DD',
      '4x3': '#95E1D3',
    };
    return colorMap[normalizedSize] || '#CCCCCC';
  };

  const orderedSizes = useMemo(() => {
    const availableSizes = new Set(
      frames.map((frame) => getFrameSizeKey(frame.size)).filter(Boolean)
    );

    const orderedByPriority = FRAME_SIZE_PRIORITY.map((size) => normalizeFrameSize(size));
    const remainingSizes = Array.from(availableSizes)
      .filter((size) => !orderedByPriority.includes(size))
      .sort((a, b) => a.localeCompare(b));

    return [...orderedByPriority, ...remainingSizes];
  }, [frames]);

  const visibleFrames = useMemo(() => {
    if (selectedFrameSizes.length === 0) {
      return frames;
    }

    const selectedSet = new Set(selectedFrameSizes.map((size) => normalizeFrameSize(size)));
    return frames.filter((frame) => selectedSet.has(getFrameSizeKey(frame.size)));
  }, [frames, selectedFrameSizes]);

  const toggleFrameSizeFilter = (size: string) => {
    setSelectedFrameSizes((prev) =>
      prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size]
    );
  };

  useEffect(() => {
    if (selectedFrameSizes.length > 0 && hasMore) {
      loadAllRemainingFrames();
    }
  }, [selectedFrameSizes, hasMore, loadAllRemainingFrames]);

  const softInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
      transition: 'background-color 0.2s, border-color 0.2s',
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.16),
      },
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        '& fieldset': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      },
      '&.Mui-focused': {
        bgcolor: '#fff',
        '& fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1.5,
        },
      },
      '& .MuiInputBase-input': {
        py: 1.35,
        px: 1.5,
      },
    },
  };

  if (loading && frames.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.25}
        sx={{ mb: { xs: 1.75, sm: 2.5 } }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="h1"
            fontWeight={800}
            sx={{
              fontSize: { xs: '1.15rem', sm: '1.75rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
            }}
          >
            {a.customFrames.title}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {visibleFrames.length} / {pagination?.total ?? frames.length} frames
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={!isMobile ? <AddIcon /> : undefined}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: { xs: 2.5, sm: 999 },
            boxShadow: 'none',
            ...(isMobile
              ? {
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  p: 0,
                }
              : { px: 2.25, py: 1 }),
          }}
          aria-label={a.customFrames.addNew}
        >
          {isMobile ? <AddIcon /> : a.customFrames.addNew}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 2 },
          mb: { xs: 1.75, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#ff8ba7', 0.28),
          background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.06)} 100%)`,
        }}
      >
        <Typography
          fontWeight={800}
          sx={{ mb: 1, fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'text.secondary' }}
        >
          {a.customFrames.filterBySize}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(5, minmax(0, 1fr))',
              sm: 'repeat(5, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))',
            },
            gap: { xs: 0.55, sm: 1 },
          }}
        >
          <Button
            size="small"
            variant={selectedFrameSizes.length === 0 ? 'contained' : 'outlined'}
            onClick={() => setSelectedFrameSizes([])}
            sx={{
              minWidth: 0,
              px: { xs: 0.25, sm: 1 },
              py: { xs: 0.7, sm: 0.75 },
              fontSize: { xs: '0.62rem', sm: '0.8rem' },
              whiteSpace: 'nowrap',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {t.common.all}
          </Button>
          {orderedSizes.map((size) => {
            const selected = selectedFrameSizes.includes(size);
            return (
              <Button
                key={size}
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => toggleFrameSizeFilter(size)}
                sx={{
                  minWidth: 0,
                  px: { xs: 0.25, sm: 1 },
                  py: { xs: 0.7, sm: 0.75 },
                  fontSize: { xs: '0.62rem', sm: '0.8rem' },
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 800,
                  lineHeight: 1.2,
                  ...(selected
                    ? {
                        bgcolor: getSizeColor(size),
                        borderColor: getSizeColor(size),
                        color: '#fff',
                        '&:hover': { bgcolor: getSizeColor(size), opacity: 0.92 },
                      }
                    : {
                        borderColor: alpha(theme.palette.primary.main, 0.35),
                        color: 'primary.main',
                      }),
                }}
              >
                {size}
              </Button>
            );
          })}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {frames.length === 0 && !loading ? (
        <Box textAlign="center" py={5}>
          <PhotoIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h6" color="textSecondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {a.customFrames.empty}
          </Typography>
        </Box>
      ) : visibleFrames.length === 0 ? (
        <Box textAlign="center" py={5}>
          <PhotoIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h6" color="textSecondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {a.customFrames.emptyFiltered}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={{ xs: 1.25, sm: 2, md: 2.5 }}>
            {visibleFrames.map((frame, index) => (
              <Grid item xs={6} sm={6} md={4} key={frame._id} sx={{ minWidth: 0 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: { xs: 2.5, sm: 3 },
                    border: '1px solid',
                    borderColor: frame.isActive ? alpha('#4caf50', 0.7) : alpha('#ff8ba7', 0.28),
                    boxShadow: frame.isActive
                      ? `0 4px 14px ${alpha('#4caf50', 0.15)}`
                      : `0 4px 14px ${alpha('#ff8ba7', 0.08)}`,
                    background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                    overflow: 'visible',
                    minWidth: 0,
                    ...userCardEnterSx(index),
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: frame.isActive
                        ? `0 6px 18px ${alpha('#4caf50', 0.2)}`
                        : `0 6px 18px ${alpha('#ff8ba7', 0.16)}`,
                      transform: { sm: 'translateY(-3px)' },
                    },
                  }}
                >
                  <Box sx={{ px: { xs: 0.75, sm: 1.25 }, pt: { xs: 1, sm: 1.5 }, overflow: 'visible' }}>
                    <CustomFrameLayoutPreview
                      width={frame.width}
                      height={frame.height}
                      size={frame.size}
                      photoPositions={frame.photoPositions}
                      maxHeight={{ xs: 160, sm: 220 }}
                    />
                  </Box>
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: { xs: 1.1, sm: 2 },
                      '&:last-child': { pb: { xs: 1.1, sm: 2 } },
                    }}
                  >
                    <Typography
                      fontWeight={800}
                      sx={{
                        color: '#e86a8a',
                        fontSize: { xs: '0.82rem', sm: '1.05rem' },
                        lineHeight: 1.3,
                        mb: 0.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {frame.name}
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mb: 0.75 }}>
                      <Chip
                        label={isMobile ? frame.size : getSizeLabel(frame.size)}
                        size="small"
                        sx={{
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: '0.62rem', sm: '0.75rem' },
                          backgroundColor: getSizeColor(frame.size),
                          color: 'white',
                          fontWeight: 800,
                        }}
                      />
                      <Chip
                        label={formatMessage(a.customFrames.photosCount, { n: frame.maxPhotos })}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: '0.62rem', sm: '0.75rem' },
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        label={frame.isActive ? a.customFrames.active : a.customFrames.inactive}
                        size="small"
                        color={frame.isActive ? 'success' : 'default'}
                        sx={{
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: '0.62rem', sm: '0.75rem' },
                          fontWeight: 700,
                        }}
                      />
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.35 }}
                    >
                      {formatMessage(a.customFrames.createdBy, { name: frame.createdBy.username })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.35 }}
                    >
                      {formatMessage(a.customFrames.created, {
                        date: new Date(frame.createdAt).toLocaleDateString(),
                      })}
                    </Typography>
                  </CardContent>

                  <CardActions
                    sx={{
                      px: { xs: 0.75, sm: 1.25 },
                      pb: { xs: 0.75, sm: 1.25 },
                      pt: 0,
                      justifyContent: 'flex-end',
                      gap: 0.25,
                    }}
                  >
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => openEditDialog(frame)}
                      title={a.customFrames.editFrame}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.25),
                        borderRadius: 2,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => openDeleteDialog(frame)}
                      title={a.customFrames.deleteFrame}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.25),
                        borderRadius: 2,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {loadingMore && (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          )}

          {hasMore && !loadingMore && <Box ref={observerTarget} sx={{ height: 20, width: '100%' }} />}

          {!hasMore && frames.length > 0 && (
            <Box textAlign="center" py={2.5}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {formatMessage(a.customFrames.endOfList, {
                  n: visibleFrames.length,
                  total: pagination?.total ?? visibleFrames.length,
                })}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Create / Edit — sheet gọn trên mobile, không fullScreen */}
      {[
        {
          open: createDialogOpen,
          onClose: () => setCreateDialogOpen(false),
          title: a.customFrames.createDialog,
          onSubmit: handleCreateFrame,
          submitLabel: a.customFrames.createFrame,
        },
        {
          open: editDialogOpen,
          onClose: () => setEditDialogOpen(false),
          title: a.customFrames.editDialog,
          onSubmit: handleUpdateFrame,
          submitLabel: a.customFrames.updateFrame,
        },
      ].map((dlg) => (
        <Dialog
          key={dlg.title}
          open={dlg.open}
          onClose={dlg.onClose}
          maxWidth="sm"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              m: { xs: 1.25, sm: 2 },
              width: { xs: 'calc(100% - 20px)', sm: '100%' },
              maxHeight: { xs: '88vh', sm: '90vh' },
              borderRadius: { xs: 3, sm: 3 },
              overflow: 'hidden',
              backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, #fff 28%)`,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.05rem', sm: '1.25rem' },
              letterSpacing: '-0.02em',
              pb: 1,
              pt: { xs: 2, sm: 2.5 },
              px: { xs: 2, sm: 3 },
            }}
          >
            {dlg.title}
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.75, sm: 2.25 },
              borderColor: alpha(theme.palette.divider, 0.7),
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {a.customFrames.frameName}
                  <Box component="span" sx={{ color: 'error.main', ml: 0.35 }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="VD: KHUNG VUÔNG"
                  required
                  inputProps={{
                    style: { fontWeight: 600, fontSize: isMobile ? '0.9rem' : '0.95rem' },
                  }}
                  sx={softInputSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {a.customFrames.size}
                  <Box component="span" sx={{ color: 'error.main', ml: 0.35 }}>
                    *
                  </Box>
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    value={formData.size}
                    displayEmpty
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, size: e.target.value as any }))
                    }
                    MenuProps={{
                      PaperProps: { sx: { maxHeight: 320, borderRadius: 2.5 } },
                    }}
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      fontWeight: 700,
                      fontSize: { xs: '0.9rem', sm: '0.95rem' },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.16),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 1.5,
                      },
                      '& .MuiSelect-select': { py: 1.35, px: 1.5 },
                    }}
                  >
                    <MenuItem value="2x4">2x4 Portrait</MenuItem>
                    <MenuItem value="4x2">4x2 Landscape</MenuItem>
                    <MenuItem value="2x6">2x6 Portrait</MenuItem>
                    <MenuItem value="6x2">6x2 Landscape</MenuItem>
                    <MenuItem value="4x6">4x6 Portrait</MenuItem>
                    <MenuItem value="6x4">6x4 Landscape</MenuItem>
                    <MenuItem value="3x4">3x4 Portrait</MenuItem>
                    <MenuItem value="4x3">4x3 Landscape</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {a.customFrames.designLink}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.link || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, link: e.target.value }))}
                  placeholder={a.customFrames.designLinkPlaceholder}
                  inputProps={{
                    style: { fontWeight: 600, fontSize: isMobile ? '0.9rem' : '0.95rem' },
                  }}
                  sx={softInputSx}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 1.25, sm: 1.75 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <PhotoPositionEditor
                  size={formData.size}
                  photoPositions={formData.photoPositions}
                  onChange={handlePhotoPositionsChange}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              gap: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              onClick={dlg.onClose}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={dlg.onSubmit}
              variant="contained"
              disabled={!formData.name}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
            >
              {dlg.submitLabel}
            </Button>
          </DialogActions>
        </Dialog>
      ))}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 2 },
            borderRadius: 3,
            width: { xs: 'calc(100% - 32px)', sm: '100%' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.2rem' } }}>
          {a.customFrames.deleteDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {a.customFrames.deleteConfirm}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleDeleteFrame}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
          >
            {t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomFramePage;
